import { Box, Button, Checkbox, Divider, Flex, Text, Tooltip } from '@rocket/ui';
import { useEffect, useState } from 'react';
import { LuStar } from 'react-icons/lu';

import Colors from '../../styles/Colors';
import PriorityListActionBar from './PriorityListActionBar';

// biome-ignore lint/suspicious/noExplicitAny: list items are caller-defined
type AnyData = any;

interface PriorityListProps {
  title?: string;
  items?: Array<AnyData>;
  selectedItems: Array<AnyData>;
  hideButtons?: boolean;
  hidePreferred?: boolean;
  getId?: (item: AnyData) => string;
  getLabel?: (item: AnyData) => string;
  onItemsChanged?: (items: Array<AnyData>) => void;
  onSelectedItemsChanged?: (
    update: Array<AnyData> | ((selected: Array<AnyData>) => Array<AnyData>)
  ) => void;
}

function PriorityList({
  title = 'Items',
  items = [],
  selectedItems,
  hideButtons = false,
  hidePreferred = false,
  getId = (f) => f.id,
  getLabel = (f) => f.name,
  onItemsChanged = (f) => f,
  onSelectedItemsChanged = (f) => f,
}: PriorityListProps) {
  const [hoverItem, setHoverItem] = useState<AnyData>();
  const [preferredItem, setPreferredItem] = useState<AnyData>();

  useEffect(() => {
    setPreferredItem(items[0]);
  }, [items]);

  function handleItemClicked(item: AnyData) {
    if (selectedItems.includes(item)) {
      onSelectedItemsChanged((selected) => selected.filter((i) => i !== item));
    } else {
      onSelectedItemsChanged((selected) => [...selected, item]);
    }
  }

  function handleCheckAll() {
    if (selectedItems.length < items.length) {
      onSelectedItemsChanged(items);
    } else {
      onSelectedItemsChanged([]);
    }
  }

  function handleSetPreferred(item: AnyData, ev: AnyData) {
    ev.stopPropagation();
    setPreferredItem(item);
    const newItems = items.filter((i) => i !== item);
    onItemsChanged([item, ...newItems]);
  }

  const allChecked = selectedItems.length > 0 && selectedItems.length === items.length;
  const someChecked = selectedItems.length > 0 && selectedItems.length < items.length;

  return (
    <Flex.H justifyContent="center" alignItems="center">
      <Box width={hideButtons ? '100%' : '66.6%'}>
        {/* Was MUI <Card> + <CardHeader avatar=…>; rocket Card's header only takes
            title/description strings, so the header is hand-rolled here. */}
        <Box borderWidth="1px" borderColor="border" borderRadius="md" overflow="hidden" bg="bg">
          <Flex.H alignItems="center" gap="8px" padding="8px 16px">
            <Checkbox
              checked={someChecked ? 'indeterminate' : allChecked}
              onChange={() => handleCheckAll()}
              disabled={items.length === 0}
              aria-label="select all items"
            />
            <Box>
              <Text fontWeight="600">{title}</Text>
              <Text fontSize="sm" color="fg.muted">
                {`${selectedItems.length}/${items.length} items selected`}
              </Text>
            </Box>
          </Flex.H>
          <Divider />
          <Box height="230px" overflow="auto" role="list">
            {items.map((listItem) => (
              <Flex.H
                key={getId(listItem)}
                role="listitem"
                onClick={() => handleItemClicked(listItem)}
                onMouseEnter={() => setHoverItem(listItem)}
                onMouseLeave={() => setHoverItem(null)}
                minHeight="46px"
                alignItems="center"
                gap="8px"
                padding="0 16px"
                cursor="pointer"
                _hover={{ bg: 'bg.subtle' }}
              >
                <Checkbox
                  checked={selectedItems.includes(listItem)}
                  tabIndex={-1}
                  aria-labelledby={getId(listItem)}
                />
                <Text id={getId(listItem)} flex="1">
                  {getLabel(listItem)}
                </Text>
                {!hidePreferred && (
                  <Tooltip
                    trigger={
                      <Button
                        variant="plain"
                        colorPalette="gray"
                        aria-label="set preferred item"
                        onClick={(e) => handleSetPreferred(listItem, e)}
                        display={
                          listItem !== hoverItem || listItem === preferredItem
                            ? 'none'
                            : 'inline-flex'
                        }
                      >
                        <LuStar />
                      </Button>
                    }
                  >
                    <Box fontSize="12px">Set as the preferred location</Box>
                  </Tooltip>
                )}
                {!hidePreferred && (
                  <Tooltip
                    trigger={
                      <Button
                        variant="plain"
                        colorPalette="gray"
                        aria-label="preferred item"
                        display={listItem !== preferredItem ? 'none' : 'inline-flex'}
                      >
                        <LuStar fill="currentColor" color={Colors.blue} />
                      </Button>
                    }
                  >
                    <Box fontSize="12px">Your preferred location</Box>
                  </Tooltip>
                )}
              </Flex.H>
            ))}
          </Box>
        </Box>
      </Box>
      {!hideButtons && (
        <Box width="33.3%">
          <PriorityListActionBar
            priorityLists={[
              {
                items,
                selectedItems,
                onItemsChanged,
                onSelectedItemsChanged,
              },
            ]}
          />
        </Box>
      )}
    </Flex.H>
  );
}

export default PriorityList;
